import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    Grid,
    Card,
    CardContent,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Box,
    CircularProgress
} from '@mui/material';
import {
    PeopleOutline,
    CheckCircleOutline,
    CancelOutlined,
    BusinessCenter
} from '@mui/icons-material';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    ArcElement,
    Title,
    Tooltip,
    Legend
} from 'chart.js';
import api from '../config/backend';
import './css/Dashboardhome.css';
import { useNavigate } from 'react-router-dom';

// Register ChartJS components
ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    ArcElement,
    Title,
    Tooltip,
    Legend
);

const Dashboard = () => {
    const [loading, setLoading] = useState(true);
    const [dashboardData, setDashboardData] = useState(null);
    const [absentEmployees, setAbsentEmployees] = useState([]);
    const token = localStorage.getItem("_crm__token");
    const navigate = useNavigate();

    useEffect(() => {
        fetchDashboardData();
        fetchAbsentEmployees();
    }, []);

    const fetchDashboardData = async () => {
        try {
            const response = await axios.post(`${api}/Dashboard/summary`, { token });
            if (response.data.success) {
                setDashboardData(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching dashboard data:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchAbsentEmployees = async () => {
        try {
            const response = await axios.post(`${api}/Dashboard/absent-employees`, { token });
            if (response.data.success) {
                setAbsentEmployees(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching absent employees:', error);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    // Prepare chart data
    const leadsChartData = {
        labels: dashboardData?.leads.last7Days.map(day => day.date) || [],
        datasets: [
            {
                label: 'Leads Captured',
                data: dashboardData?.leads.last7Days.map(day => day.count) || [],
                fill: false,
                backgroundColor: 'rgba(75, 192, 192, 0.2)',
                borderColor: 'rgba(75, 192, 192, 1)',
                tension: 0.1
            }
        ]
    };

    const attendanceChartData = {
        labels: ['Present', 'Absent'],
        datasets: [
            {
                data: [
                    dashboardData?.attendance.present || 0,
                    dashboardData?.attendance.absent || 0
                ],
                backgroundColor: [
                    'rgba(75, 192, 192, 0.6)',
                    'rgba(255, 99, 132, 0.6)'
                ],
                borderColor: [
                    'rgba(75, 192, 192, 1)',
                    'rgba(255, 99, 132, 1)'
                ],
                borderWidth: 1
            }
        ]
    };

    const leadsBarData = {
        labels: dashboardData?.leads.byEmployee.map(item => item.employeeName).slice(0, 5) || [],
        datasets: [
            {
                label: 'Leads Captured Today',
                data: dashboardData?.leads.byEmployee.map(item => item.count).slice(0, 5) || [],
                backgroundColor: 'rgba(54, 162, 235, 0.6)',
                borderColor: 'rgba(54, 162, 235, 1)',
                borderWidth: 1
            }
        ]
    };

    return (
        <div className="dashboard-container">
            <Typography variant="h4" className="dashboard-title">
                Dashboard
            </Typography>

            {/* Summary Cards */}
            <Grid container spacing={3} className="summary-cards" >
                <Grid item xs={12} sm={6} md={3}>
                    <Card className="summary-card" onClick={() => {
                        // handle click event
                        navigate('/Employee');
                    }}>
                        <CardContent>
                            <div className="card-icon employee-icon">
                                <PeopleOutline />
                            </div>
                            <div className="card-content">
                                <Typography variant="h5">{dashboardData?.totalEmployees || 0}</Typography>
                                <Typography variant="body2">Total Employees</Typography>
                            </div>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <Card className="summary-card" onClick={() => {
                        // handle click event
                        navigate('/Attendence/present');
                    }}>
                        <CardContent>
                            <div className="card-icon present-icon">
                                <CheckCircleOutline />
                            </div>
                            <div className="card-content">
                                <Typography variant="h5">{dashboardData?.attendance.present || 0}</Typography>
                                <Typography variant="body2">Present Today</Typography>
                            </div>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <Card className="summary-card" onClick={() => {
                        // handle click event
                        navigate('/Attendence/absent');
                    }}>
                        <CardContent>
                            <div className="card-icon absent-icon">
                                <CancelOutlined />
                            </div>
                            <div className="card-content">
                                <Typography variant="h5">{dashboardData?.attendance.absent || 0}</Typography>
                                <Typography variant="body2">Absent Today</Typography>
                            </div>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <Card className="summary-card" onClick={() => {
                        // handle click event
                        navigate('/Leads');
                    }}>
                        <CardContent>
                            <div className="card-icon leads-icon">
                                <BusinessCenter />
                            </div>
                            <div className="card-content">
                                <Typography variant="h5">{dashboardData?.leads.todayTotal || 0}</Typography>
                                <Typography variant="body2">Leads Today</Typography>
                            </div>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Charts Section */}
            <Grid container spacing={3} className="charts-section">
                <Grid item xs={12} md={8}>
                    <Card className="chart-card">
                        <CardContent>
                            <Typography variant="h6" className="chart-title">
                                Leads Captured (Last 7 Days)
                            </Typography>
                            <div className="chart-container">
                                <Line data={leadsChartData} options={{ maintainAspectRatio: false }} />
                            </div>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={4}>
                    <Card className="chart-card">
                        <CardContent>
                            <Typography variant="h6" className="chart-title">
                                Today's Attendance
                            </Typography>
                            <div className="chart-container">
                                <Doughnut data={attendanceChartData} options={{ maintainAspectRatio: false }} />
                            </div>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12}>
                    <Card className="chart-card">
                        <CardContent>
                            <Typography variant="h6" className="chart-title">
                                Top Performers (Leads Captured Today)
                            </Typography>
                            <div className="chart-container">
                                <Bar data={leadsBarData} options={{ maintainAspectRatio: false }} />
                            </div>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Absent Employees Table */}
            <Grid container spacing={3} className="tables-section">
                <Grid item xs={12}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" className="table-title">
                                Absent Employees Today
                            </Typography>
                            <TableContainer component={Paper} className="table-container">
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Employee Name</TableCell>
                                            <TableCell>Email</TableCell>
                                            <TableCell>Designation</TableCell>
                                            <TableCell>Employee Code</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {absentEmployees.length > 0 ? (
                                            absentEmployees.map((employee) => (
                                                <TableRow key={employee._id}>
                                                    <TableCell>
                                                        {employee.firstName} {employee.lastName}
                                                    </TableCell>
                                                    <TableCell>{employee.company_email || employee.personal_email || '-'}</TableCell>
                                                    <TableCell>{employee.Designation || '-'}</TableCell>
                                                    <TableCell>{employee.Employee_code || '-'}</TableCell>
                                                </TableRow>
                                            ))
                                        ) : (
                                            <TableRow>
                                                <TableCell colSpan={4} align="center">
                                                    No absent employees today
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </div>
    );
};

export default Dashboard;
